import { motion } from 'framer-motion';
import { MapPin, ArrowUpRight, Navigation } from 'lucide-react';
import { MaskedRevealOnView, FadeUp } from './motion/Reveal';
import { BRAND } from '../data/content';

// Public share link (user-provided)
const MAPS_SHARE = 'https://maps.app.goo.gl/S3RCpa25vdt4uNVo8?g_st=ic';
// Address-based embed (no API key required)
const MAPS_EMBED = 'https://maps.google.com/maps?q=Indian%20Industrial%20Distributors%2C%20Plot%20D8%20Jai%20Bhavani%20Nagar%2C%20Wagle%20Industrial%20Estate%2C%20Thane%20400604&t=&z=16&ie=UTF8&iwloc=&output=embed';

const FindUs = () => {
    return (
        <section id="find-us" data-testid="findus-section" className="bg-[#0A0A0B] text-white py-24 md:py-32 border-t border-white/10 relative overflow-hidden">
            <div
                className="absolute inset-0 pointer-events-none opacity-[0.04]"
                style={{
                    backgroundImage:
                        'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
                    backgroundSize: '80px 80px',
                }}
            />

            <div className="relative mx-auto max-w-[1500px] px-6 md:px-10">
                <div className="grid grid-cols-12 gap-6 mb-14">
                    <div className="col-span-12 md:col-span-4">
                        <span className="overline text-[#FF3B00]">Find us · Thane</span>
                    </div>
                    <div className="col-span-12 md:col-span-8">
                        <MaskedRevealOnView
                            className="h-display text-[10vw] md:text-[6vw] leading-[0.9]"
                            lines={['The plant.', 'Come see it.']}
                        />
                        <FadeUp delay={0.15}>
                            <p className="mt-6 max-w-2xl text-white/70 text-base md:text-lg leading-relaxed">
                                Wagle Industrial Estate — the beating heart of Thane's precision-manufacturing corridor. Walk-ins welcome by appointment.
                            </p>
                        </FadeUp>
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-6 lg:gap-10 border-t border-white/15 pt-10">
                    {/* Info column */}
                    <div className="col-span-12 lg:col-span-5">
                        <FadeUp>
                            <div className="overline text-white/50 mb-4">Address</div>
                            <div className="h-display text-2xl md:text-3xl leading-[1.15] mb-8">
                                {BRAND.addressLine1}<br />
                                {BRAND.addressLine2}<br />
                                {BRAND.addressLine3}
                            </div>

                            <div className="grid grid-cols-2 border-t border-white/15">
                                <div className="p-4 border-r border-white/15">
                                    <div className="overline text-white/50 mb-2">Coordinates</div>
                                    <div className="font-mono text-sm">19.2011° N<br />72.9615° E</div>
                                </div>
                                <div className="p-4">
                                    <div className="overline text-white/50 mb-2">Hours</div>
                                    <div className="font-mono text-sm">Mon – Sat<br />09:30 – 18:30 IST</div>
                                </div>
                            </div>

                            <a
                                href={MAPS_SHARE}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="get-directions"
                                className="btn-invert mt-8 inline-flex items-center gap-3 h-14 px-8 bg-[#FF3B00] text-white text-[11px] font-mono uppercase tracking-[0.24em] hover:text-[#FF3B00]"
                            >
                                <Navigation size={14} strokeWidth={2} />
                                Get Directions
                                <ArrowUpRight size={16} />
                            </a>

                            <div className="mt-6 flex items-center gap-3 overline text-white/40">
                                <MapPin size={12} />
                                <a
                                    href={`tel:${BRAND.phones[0].replace(/\s/g, '')}`}
                                    className="hover:text-white"
                                    data-testid="findus-phone"
                                >
                                    {BRAND.phones[0]} · ring for appointment
                                </a>
                            </div>
                        </FadeUp>
                    </div>

                    {/* Map column */}
                    <motion.div
                        initial={{ opacity: 0, scale: 1.02 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, margin: '-60px' }}
                        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                        className="col-span-12 lg:col-span-7 relative overflow-hidden clip-corner"
                    >
                        <div className="aspect-[4/3] md:aspect-[16/10] relative bg-[#111112]">
                            <iframe
                                src={MAPS_EMBED}
                                data-testid="google-map-embed"
                                title="Indian Industrial Distributors — Thane"
                                width="100%"
                                height="100%"
                                loading="lazy"
                                allowFullScreen
                                referrerPolicy="no-referrer-when-downgrade"
                                className="absolute inset-0 w-full h-full border-0"
                                style={{
                                    filter: 'grayscale(1) contrast(0.9) brightness(0.85)',
                                    transition: 'filter 500ms ease',
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.filter = 'grayscale(0) contrast(1) brightness(1)')}
                                onMouseLeave={(e) => (e.currentTarget.style.filter = 'grayscale(1) contrast(0.9) brightness(0.85)')}
                            />
                            {/* Corner label */}
                            <div className="absolute top-4 left-4 z-10 bg-[#0A0A0B] border border-white/20 px-4 py-2 pointer-events-none">
                                <div className="overline text-[#FF3B00]">Live location</div>
                                <div className="font-mono text-xs text-white/80 mt-0.5">Wagle Estate · Thane</div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default FindUs;
